import CryptoJS from 'crypto-js';

/**
 * Servicio único de encriptación AES
 * Elimina duplicación entre encryption.util.ts y baseEncryption.service.ts
 * Responsabilidad única: Operaciones básicas de encriptación
 * Cumple Single Responsibility Principle
 */
export class EncryptionService {
  private static readonly CRYPTO_KEY = process.env.PUBLIC_SECRET_KEY;

  static {
    if (!this.CRYPTO_KEY) {
      throw new Error('Missing PUBLIC_SECRET_KEY in environment variables');
    }
  }

  /**
   * Encripta un valor usando AES-256
   * @param value - Valor a encriptar
   * @returns Valor encriptado o null si input es null
   */
  static encrypt(value: string | null): string | null {
    if (value === null) return null;
    return CryptoJS.AES.encrypt(value, this.CRYPTO_KEY as string).toString();
  }

  /**
   * Desencripta un valor AES-256
   * @param encryptedValue - Valor encriptado
   * @returns Valor desencriptado o null si input es null
   */
  static decrypt(encryptedValue: string | null): string | null {
    if (encryptedValue === null) return null;
    try {
      const bytes = CryptoJS.AES.decrypt(
        encryptedValue,
        this.CRYPTO_KEY as string,
      );
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      throw new Error('Decryption failed: Invalid encrypted value');
    }
  }

  /**
   * Encripta un valor booleano
   * @param value - Valor booleano a encriptar
   * @returns Valor encriptado o null si input es null
   */
  static encryptBoolean(value: boolean | null): string | null {
    if (value === null) return null;
    return this.encrypt(String(value)); // "true" o "false"
  }

  /**
   * Desencripta un valor booleano
   * @param encryptedValue - Valor encriptado
   * @returns Valor booleano desencriptado o null si input es null
   */
  static decryptBoolean(encryptedValue: string | null): boolean | null {
    if (encryptedValue === null) return null;
    const decrypted = this.decrypt(encryptedValue);
    if (decrypted === null) return null;
    return decrypted === 'true';
  }

  /**
   * Verifica si un valor parece estar encriptado
   * @param value - Valor a verificar
   */
  static isEncrypted(value: string | null): boolean {
    if (!value) return false;
    // Los valores encriptados de CryptoJS tienen un formato específico
    return /^[A-Za-z0-9+/]+=*$/.test(value) && value.length > 20;
  }
}
